import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  BookOpen, Server, HardDrive,
  Clock, CheckCircle, ExternalLink,
} from 'lucide-react';
import { organizationsAPI, projectsAPI } from '../../services/api';
import { getStatusLabel, getStatusBadgeClass } from '../../utils/statusHelpers';
import useAuthStore from '../../context/authStore';
import { hasUserModuleAccess, CLIENT_PORTAL_MODULES } from '../../utils/clientPortalPermissions';

const HOSTING_LABELS = {
  stratel: 'Hosted on Stratel LMS',
  external: "Client's LMS — SCORM Delivery",
};

const CCDA_STATUS_COPY = {
  draft: { label: 'Draft', color: 'text-gray-500', desc: 'Agreement is being prepared.' },
  sent: { label: 'Sent for Signature', color: 'text-blue-600', desc: 'Please review and sign the CCDA.' },
  signed: { label: 'Signed', color: 'text-green-600', desc: 'Agreement in effect. Work is underway.' },
  declined: { label: 'Declined', color: 'text-red-600', desc: 'Agreement was declined. Contact your consultant.' },
};

function StatusStep({ label, done, active }) {
  return (
    <div className={`flex items-center gap-2 text-sm ${active ? 'font-semibold text-teal-700' : done ? 'text-gray-400 line-through' : 'text-gray-600'}`}>
      {done ? (
        <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
      ) : active ? (
        <div className="w-4 h-4 rounded-full border-2 border-teal-600 flex-shrink-0" />
      ) : (
        <div className="w-4 h-4 rounded-full border-2 border-gray-300 flex-shrink-0" />
      )}
      {label}
    </div>
  );
}

const STATUS_STEPS = [
  { key: 'active', label: 'Project started' },
  { key: 'in_progress', label: 'Development in progress' },
  { key: 'in_review', label: 'Under review / acceptance' },
  { key: 'completed', label: 'Completed & delivered' },
];

const STATUS_ORDER = ['active', 'in_progress', 'in_review', 'completed'];

function ProjectCard({ project }) {
  const hostingIcon = project.hostingType === 'stratel' ? Server : HardDrive;
  const HostingIcon = hostingIcon;
  const ccdaDocs = (project.engagementDocuments || []).filter(d => d.type === 'ccda');
  const activeCcda = ccdaDocs.find(d => d.status !== 'superseded') || ccdaDocs[0];
  const ccdaInfo = activeCcda ? CCDA_STATUS_COPY[activeCcda.status] : null;

  const currentStatusIdx = STATUS_ORDER.indexOf(project.status);

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      {/* Card header */}
      <div className="p-5 border-b border-gray-100">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
              <BookOpen className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">{project.name}</h3>
              <div className="flex items-center gap-1.5 mt-0.5 text-xs text-gray-500">
                <HostingIcon className="w-3.5 h-3.5" />
                {HOSTING_LABELS[project.hostingType] || 'Unknown hosting'}
              </div>
            </div>
          </div>
          <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${getStatusBadgeClass(project.status)}`}>
            {getStatusLabel(project.status)}
          </span>
        </div>
      </div>

      {/* CCDA status */}
      {ccdaInfo && (
        <div className="px-5 py-3 bg-gray-50 border-b border-gray-100">
          <div className={`text-sm font-medium ${ccdaInfo.color}`}>{ccdaInfo.label}</div>
          <p className="text-xs text-gray-500 mt-0.5">{ccdaInfo.desc}</p>
          {activeCcda?.status === 'sent' && (
            <Link
              to={`/signatures`}
              className="inline-flex items-center gap-1 mt-2 text-xs font-medium text-teal-600 hover:text-teal-700"
            >
              Review &amp; Sign Agreement <ExternalLink className="w-3 h-3" />
            </Link>
          )}
        </div>
      )}

      {/* Progress steps */}
      <div className="p-5">
        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">Progress</p>
        <div className="space-y-2">
          {STATUS_STEPS.map((step, idx) => (
            <StatusStep
              key={step.key}
              label={step.label}
              done={idx < currentStatusIdx}
              active={idx === currentStatusIdx}
            />
          ))}
        </div>
      </div>

      {/* Stratel-hosted: review course link */}
      {project.hostingType === 'stratel' && project.courseId && (
        <div className="px-5 pb-5">
          <div className="rounded-lg bg-purple-50 border border-purple-200 p-4">
            <p className="text-sm font-semibold text-purple-800 mb-1">Review Your Course</p>
            <p className="text-xs text-gray-600 mb-3">
              Your custom course is available for review in the training catalog. Take it to experience
              the content and provide feedback before formal acceptance.
            </p>
            <Link
              to={`/training/${project.courseId}`}
              className="inline-flex items-center gap-1.5 text-sm font-medium text-purple-700 hover:text-purple-800"
            >
              <ExternalLink className="w-4 h-4" />
              Take Draft Course
            </Link>
          </div>
        </div>
      )}

      {/* External: SCORM delivery info */}
      {project.hostingType === 'external' && project.status === 'in_review' && (
        <div className="px-5 pb-5">
          <div className="rounded-lg bg-blue-50 border border-blue-200 p-4">
            <p className="text-sm font-semibold text-blue-800 mb-1">Delivery Ready</p>
            <p className="text-xs text-gray-600 mb-3">
              Your SCORM package and source files are ready for delivery. Review the documents below
              and sign the acceptance form to complete this engagement.
            </p>
            <Link
              to="/documents"
              className="inline-flex items-center gap-1.5 text-sm font-medium text-blue-700 hover:text-blue-800"
            >
              <ExternalLink className="w-4 h-4" />
              View Delivery Documents
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}

export default function CourseDevPage() {
  const { user } = useAuthStore();

  const { data: orgData } = useQuery({
    queryKey: ['client-organization-me'],
    queryFn: () => organizationsAPI.getMe(),
    enabled: Boolean(user),
  });

  const organizationId = orgData?.data?.organization?._id;

  const { data: projects = [], isLoading } = useQuery({
    queryKey: ['lms-projects-client'],
    queryFn: () => projectsAPI.getAll({ serviceType: 'lms' }),
    select: (res) => (res.projects || res.data?.projects || []).filter(p => p.serviceType === 'lms'),
    enabled: Boolean(user),
  });

  // Service change requests live in Settings - Service Access, which only some roles can open
  const canRequestChanges = hasUserModuleAccess(user, CLIENT_PORTAL_MODULES.service_change, 'view');

  const { data: serviceRequests = [] } = useQuery({
    queryKey: ['lms-service-change-requests', organizationId],
    queryFn: () => organizationsAPI.getServiceChangeRequests(organizationId, { limit: 20 }),
    select: (res) => (res.data?.requests || res.requests || []).filter((item) => item?.requestedServices?.lms === true),
    enabled: Boolean(organizationId) && canRequestChanges,
  });

  const openRequests = serviceRequests.filter(
    (item) => ['pending', 'commercial_pending'].includes(item.status) && item?.lmsCustomCourseRequest
  );

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
        <h1 className="text-2xl font-bold text-gray-900">Course Development</h1>
        <p className="text-sm text-gray-500 mt-1">
          Track your custom course development engagement with Stratel.
        </p>
        </div>
        {canRequestChanges && (
          <Link
            to="/settings?tab=organization&orgTab=service-access"
            className="inline-flex items-center gap-2 rounded-lg bg-teal-700 px-4 py-2 text-sm font-medium text-white hover:bg-teal-600"
          >
            Request LMS Change
          </Link>
        )}
      </div>

      <div className="rounded-xl border border-teal-200 bg-teal-50 p-4">
        <p className="text-sm font-semibold text-teal-900">New LMS or custom development requests now start in Service Access.</p>
        {canRequestChanges ? (
          <p className="mt-1 text-sm text-teal-800">
            Use <Link to="/settings?tab=organization&orgTab=service-access" className="font-medium underline">Settings - Service Access</Link> to request standard LMS changes, custom course development, or both.
          </p>
        ) : (
          <p className="mt-1 text-sm text-teal-800">
            Ask your organization administrator to request LMS changes or custom course development.
          </p>
        )}
      </div>

      {openRequests.length > 0 && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-4">
          <div className="flex items-center gap-2 text-amber-900">
            <Clock className="w-4 h-4" />
            <p className="text-sm font-semibold">Open LMS expansion request</p>
          </div>
          <div className="mt-2 space-y-2">
            {openRequests.map((item) => (
              <div key={item._id} className="rounded-lg border border-amber-100 bg-white px-3 py-2 text-sm text-gray-700">
                <div className="font-medium text-gray-900">{item.lmsCustomCourseRequest?.courseTitle || 'Custom Course Request'}</div>
                <div className="text-xs text-gray-500 mt-1">
                  Status: {item.status} • Submitted {new Date(item.createdAt).toLocaleDateString()}
                </div>
                <div className="mt-1 text-xs text-gray-600">{item.reason}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {isLoading ? (
        <div className="text-center py-12 text-gray-400">Loading…</div>
      ) : projects.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-xl border border-gray-200">
          <BookOpen className="w-10 h-10 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500">No custom course development projects found.</p>
          <p className="text-sm text-gray-400 mt-1">
            Contact your Stratel consultant to get started.
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {projects.map(p => <ProjectCard key={p._id} project={p} />)}
        </div>
      )}
    </div>
  );
}
